import Vue from "vue";

export default Vue.extend({
  name: "SvgDiagram",
  props: {
    /** SVG data URI or inline SVG. */
    svgData: {
      type: String,
      required: true
    },
    /** Current zoom level (1 = 100%). */
    zoomLevel: {
      type: Number,
      default: 1
    },
    /** ItemNumber whose matching hotspot (`<g hsid="..." name="ItemNumber">`) should be highlighted. */
    highlightedItemNumber: {
      type: [String, Number],
      default: null
    },
    /**
     * ItemNumbers that have a corresponding part in the results list. Hotspots
     * whose `name` isn't in this list are rendered grey and show a
     * "not serviceable" popup instead of highlighting on click. When omitted,
     * every hotspot is treated as serviceable.
     */
    validItemNumbers: {
      type: Array,
      default: null
    }
  },
  data: () => ({
    isDragging: false,
    dragStartX: 0,
    dragStartY: 0,
    dragStartTranslateX: 0,
    dragStartTranslateY: 0,
    translateX: 0,
    translateY: 0,
    /** Disables the transform transition while a zoom level change is being measured for focusHotspot(). */
    suppressTransition: false,
    showUnserviceablePopup: false,
    unserviceablePopupPosition: { x: 0, y: 0 },
    unserviceablePopupTimeoutId: null as any,
  }),
  computed: {
    effectiveZoom(): number {
      return this.zoomLevel * 0.75;
    },
    /** Computed transform style for pan functionality. */
    containerStyle(): any {
      return {
        transform: `translate(${this.translateX}px, ${this.translateY}px) scale(${this.effectiveZoom})`,
        transformOrigin: "top left",
        cursor: this.isDragging ? "grabbing" : "grab",
        transition: (this.isDragging || this.suppressTransition) ? "none" : "transform 0.2s ease-out"
      };
    }
  },
  watch: {
    svgData() {
      this.$nextTick(() => {
        this.centerDiagram();
        this.applyHotspotStates();
      });
    },
    highlightedItemNumber() {
      this.$nextTick(this.applyHotspotStates);
    },
    validItemNumbers() {
      this.$nextTick(this.applyHotspotStates);
    }
  },
  mounted() {
    const content = this.$refs.content as HTMLElement | undefined;
    if (content) {
      content.addEventListener("click", this.onContentClick);
    }
    this.$nextTick(() => {
      this.centerDiagram();
      this.applyHotspotStates();
    });
  },
  beforeDestroy() {
    const content = this.$refs.content as HTMLElement | undefined;
    if (content) {
      content.removeEventListener("click", this.onContentClick);
    }
    document.removeEventListener("mousemove", this.onDrag as any);
    document.removeEventListener("mouseup", this.endDrag as any);
    if (this.unserviceablePopupTimeoutId) {
      clearTimeout(this.unserviceablePopupTimeoutId);
    }
  },
  methods: {
    /** Returns the viewport element used to size and center the diagram. */
    getViewport(): HTMLElement | undefined {
      return this.$el as HTMLElement | undefined;
    },

    /** Returns the transform container element that owns the diagram size. */
    getContainer(): HTMLElement | undefined {
      return this.$refs.container as HTMLElement | undefined;
    },

    /** Centers the diagram within the viewport using the current zoom level. */
    centerDiagram() {
      const viewport = this.getViewport();
      const container = this.getContainer();
      if (!viewport || !container) return;

      const viewportWidth = viewport.clientWidth;
      const viewportHeight = viewport.clientHeight;
      const containerWidth = container.offsetWidth;
      const containerHeight = container.offsetHeight;

      this.translateX = (viewportWidth - containerWidth * this.effectiveZoom) / 2;
      this.translateY = (viewportHeight - containerHeight * this.effectiveZoom) / 2;
    },

    /** Zooms while keeping the point under the cursor fixed in the viewport. */
    zoomToPoint(nextZoomLevel: number, clientX: number, clientY: number) {
      const viewport = this.getViewport();
      const container = this.getContainer();
      if (!viewport || !container) return;

      const viewportRect = viewport.getBoundingClientRect();
      const currentScale = this.effectiveZoom;
      const nextScale = nextZoomLevel * 0.75;

      if (nextScale < currentScale) {
        const imageCenterX = this.translateX + (container.offsetWidth * currentScale) / 2;
        const imageCenterY = this.translateY + (container.offsetHeight * currentScale) / 2;
        const targetX = viewportRect.width / 2;
        const targetY = viewportRect.height / 2;
        const contentX = (imageCenterX - this.translateX) / currentScale;
        const contentY = (imageCenterY - this.translateY) / currentScale;

        this.translateX = targetX - contentX * nextScale;
        this.translateY = targetY - contentY * nextScale;
        return;
      }

      const imageLeft = this.translateX;
      const imageTop = this.translateY;
      const imageRight = this.translateX + container.offsetWidth * currentScale;
      const imageBottom = this.translateY + container.offsetHeight * currentScale;

      const cursorX = Math.min(Math.max(clientX - viewportRect.left, imageLeft), imageRight);
      const cursorY = Math.min(Math.max(clientY - viewportRect.top, imageTop), imageBottom);

      const contentX = (cursorX - this.translateX) / currentScale;
      const contentY = (cursorY - this.translateY) / currentScale;

      this.translateX = cursorX - contentX * nextScale;
      this.translateY = cursorY - contentY * nextScale;
    },

    /** Start panning when user clicks and drags on the diagram. */
    startDrag(event: MouseEvent) {
      if (event.button !== 0) return; // Only left-click
      this.isDragging = true;
      this.dragStartX = event.clientX;
      this.dragStartY = event.clientY;
      this.dragStartTranslateX = this.translateX;
      this.dragStartTranslateY = this.translateY;

      document.addEventListener("mousemove", this.onDrag as any);
      document.addEventListener("mouseup", this.endDrag as any);
    },

    /** Handle panning as user drags. */
    onDrag(event: MouseEvent) {
      if (!this.isDragging) return;

      const deltaX = event.clientX - this.dragStartX;
      const deltaY = event.clientY - this.dragStartY;
      this.translateX = this.dragStartTranslateX + deltaX;
      this.translateY = this.dragStartTranslateY + deltaY;
    },

    /** Stop panning. */
    endDrag() {
      this.isDragging = false;
      document.removeEventListener("mousemove", this.onDrag as any);
      document.removeEventListener("mouseup", this.endDrag as any);
    },

    /** Reset pan position to a centered default view. */
    resetPan() {
      this.$nextTick(() => {
        this.centerDiagram();
      });
    },

    /**
     * Delegated click handler for hotspot elements. SmartEquip diagrams render
     * each callout as `<g hsid="..." name="ItemNumber"><rect/><text/></g>` — the
     * `name` attribute matches a part's ItemNumber. Hotspots with no matching
     * part (see validItemNumbers) show a "not serviceable" popup instead.
     */
    onContentClick(event: MouseEvent) {
      const target = event.target as HTMLElement;
      const hotspot = target.closest("[hsid]") as HTMLElement | null;
      if (!hotspot) return;

      const itemNumber = hotspot.getAttribute("name");
      if (!itemNumber) return;

      if (!this.isItemNumberServiceable(itemNumber)) {
        this.flashUnserviceablePopup(hotspot);
        return;
      }

      this.$emit("hotspot-click", itemNumber);
    },

    /** True when validItemNumbers wasn't provided, or when it contains the given ItemNumber. */
    isItemNumberServiceable(itemNumber: string): boolean {
      if (!Array.isArray(this.validItemNumbers)) return true;
      return this.validItemNumbers.some((n: any) => String(n) === itemNumber);
    },

    /** Briefly shows the "not serviceable" popup, anchored just above the given hotspot. */
    flashUnserviceablePopup(hotspot: HTMLElement) {
      const viewport = this.$el as HTMLElement;
      const hotspotRect = hotspot.getBoundingClientRect();
      const viewportRect = viewport.getBoundingClientRect();

      this.unserviceablePopupPosition = {
        x: hotspotRect.left + hotspotRect.width / 2 - viewportRect.left,
        y: hotspotRect.top - viewportRect.top
      };

      if (this.unserviceablePopupTimeoutId) {
        clearTimeout(this.unserviceablePopupTimeoutId);
      }
      this.showUnserviceablePopup = true;
      this.unserviceablePopupTimeoutId = setTimeout(() => {
        this.showUnserviceablePopup = false;
        this.unserviceablePopupTimeoutId = null;
      }, 3000);
    },

    /** Toggles the highlight/unserviceable classes on each hotspot based on highlightedItemNumber and validItemNumbers. */
    applyHotspotStates() {
      const content = this.$refs.content as HTMLElement | undefined;
      if (!content) return;

      const hotspots = content.querySelectorAll("[hsid]");
      hotspots.forEach((el) => {
        const name = el.getAttribute("name");
        const isServiceable = name != null && this.isItemNumberServiceable(name);
        const isHighlighted =
          isServiceable &&
          this.highlightedItemNumber != null &&
          name === String(this.highlightedItemNumber);

        el.classList.toggle("svg-diagram__hotspot--unserviceable", !isServiceable);
        el.classList.toggle("svg-diagram__hotspot--highlighted", isHighlighted);
      });
    },

    /**
     * Pans (and, via the parent-controlled `zoomLevel` prop, zooms) so the hotspot
     * matching the given ItemNumber is centered in the viewport. Assumes the caller
     * has already updated `zoomLevel` — the transition is briefly suppressed so the
     * new zoom is measured accurately before panning.
     */
    async focusHotspot(itemNumber: string | number) {
      const content = this.$refs.content as HTMLElement | undefined;
      const viewport = this.$el as HTMLElement;
      if (!content || !viewport) return;

      const hotspot = Array.from(content.querySelectorAll("[hsid]"))
        .find((el) => el.getAttribute("name") === String(itemNumber)) as HTMLElement | undefined;
      if (!hotspot) return;

      // Suppress the transition so the zoom level change and this measurement
      // both reflect the same, un-animated layout.
      this.suppressTransition = true;
      await this.$nextTick();

      const hotspotRect = hotspot.getBoundingClientRect();
      const viewportRect = viewport.getBoundingClientRect();

      const deltaX = (viewportRect.left + viewportRect.width / 2 - (hotspotRect.left + hotspotRect.width / 2)) / this.effectiveZoom;
      const deltaY = (viewportRect.top + viewportRect.height / 2 - (hotspotRect.top + hotspotRect.height / 2)) / this.effectiveZoom;

      // Re-enable the transition so the pan itself animates smoothly.
      this.suppressTransition = false;
      this.translateX += deltaX;
      this.translateY += deltaY;
    }
  }
});

